import moment, { getCurrentTimezone } from '@/utils/moment-wrapper';
import isDate from 'lodash/isDate';
import isNumber from 'lodash/isNumber';

const elementFormats = [
    { value: 'w3c_iso8601', format: '' },
    { value: 'seconds', format: '' },
    { value: 'milliseconds', format: '' },
    { value: 'format:Mon Jan 02 2006', format: 'ddd MMM DD YYYY' },
    { value: 'format:01/02/2006 03:04pm', format: 'MM/DD/YY hh:mma', invalidDateParse: true },
    { value: 'format:Mon Jan 02 2006 15:04:05 MST-0700', format: 'ddd MMM DD YYYY HH:mm:ss zZZ' },
    { value: 'format:2006-01-02T15:04:05-0700', format: 'YYYY-MM-DDTHH:mm:ssZZ' },
    { value: 'format:2006-01-02T15:04:05-07:00', format: 'YYYY-MM-DDTHH:mm:ssZ' },
    { value: 'format:2006-01-02T15:04:05.999-07:00', format: 'YYYY-MM-DDTHH:mm:ss.SSSZZ' },
    { value: 'format:2006-01-02T15:04:05Z07:00', format: 'YYYY-MM-DDTHH:mm:ss[Z]Z', invalidDateParse: true },
    { value: 'format:2006-01-02T15:04:05.99Z', format: 'YYYY-MM-DDTHH:mm:ss.SS[Z]', invalidDateParse: true },
    { value: 'format:2006-01-02T15:04:05', format: 'YYYY-MM-DDTHH:mm:ss' },
    { value: 'format:2006-01-02 15:04:05 -0700', format: 'YYYY-MM-DD HH:mm:ss Z', invalidDateParse: true },
    { value: 'format:2006-01-02 15:04:05 MST', format: 'YYYY-MM-DD HH:mm:ss z' },
    { value: 'format:2006-01-02 15:04:05', format: 'YYYY-MM-DD HH:mm:ss' },
];

// Many of the calls to the angular subscriptionDateFilter pass particular strings recognized by the angular date filter
// The angular subscriptionDateFilter calls this file (see '@/data/subscription-time.js) so we use this map to support those strings.
// Angular date format tokens do not always match moment tokens
// This maps the angular shortcuts to what produces the equivalent result with momentjs.
// Momentjs Docs - https://momentjscom.readthedocs.io/en/latest/moment/04-displaying/01-format/
// Angular Date Filter docs - https://docs.angularjs.org/api/ng/filter/date

const angularDateFilterFormatMap = {
    short: 'M/D/YY h:mm A', // Angular: M/d/yy h:mm a
    medium: 'MMM D, Y h:mm:ss A', // Angular: MMM d, y h:mm:ss a
    shortDate: 'M/D/YY', // Angular: M/d/yy
    mediumDate: 'MMM D, Y', // Angular: MMM d, y
    longDate: 'MMMM D, Y', // Angular: MMMM d, y
    fullDate: 'EEEE, MMMM D, Y', // Angular: EEEE, MMMM d, y
    shortTime: 'h:mm A', // Angular: h:mm a
    mediumTime: 'h:mm:ss A', // Angular: h:mm:ss a
};

const parseDate = function (datetime) {
    if (isDate(datetime)) {
        return new Date(datetime.getTime());
    }
    const timestamp = parseInt(datetime, 10);
    if (isNumber(timestamp) && !isNaN(timestamp)) {
        return new Date(timestamp);
    }
};

const offsetFromLocalTime = function (time) {
    const localTimezoneOffset = time.getTimezoneOffset();
    let subscriptionTimezoneOffset = localTimezoneOffset;

    const currentTz = getCurrentTimezone();
    if (currentTz) {
        // moment(...).utcOffset() returns minutes offset from UTC.
        // Date#getTimezoneOffset() is the opposite sign, so invert.
        const momentOffsetMinutes = moment(time).utcOffset();
        subscriptionTimezoneOffset = -momentOffsetMinutes;
    }

    const ret = (subscriptionTimezoneOffset - localTimezoneOffset) * 60 * 1000;

    return ret;
};

export function getStartOfDay (datetime) {
    if (datetime == null) {
        datetime = new Date();
    }

    let result = parseDate(datetime);

    if (result) {
        const offset = offsetFromLocalTime(result);
        result.setHours(0, 0, 0, 0);
        result = new Date(result.getTime() + offset);
    }

    return result;
}

export function getTimeOfDay (currentTime) {
    // time of day
    const h = currentTime.getHours();

    // 12am -> 5:59am, 10pm -> 11:59pm (night)
    if ((h >= 0 && h < 6) || h >= 22) {
        return 0;
    }
    // 6am -> 11:59am (morning)
    if (h >= 6 && h < 12) {
        return 1;
    }
    // 12pm -> 5:59pm (afternoon)
    if (h >= 12 && h < 18) {
        return 2;
    }
    // 6pm -> 9:59pm
    if (h >= 18 && h < 22) {
        return 3;
    }
}

export function getStartOfWeek (datetime) {
    if (datetime == null) {
        datetime = new Date();
    }

    const result = parseDate(datetime);

    if (result) {
        result.setDate(result.getDate() - result.getDay());

        return getStartOfDay(result);
    }

    return result;
}

export function getStartOfMonth (datetime) {
    if (datetime == null) {
        datetime = new Date();
    }

    const result = parseDate(datetime);

    if (result) {
        result.setDate(1);

        return getStartOfDay(result);
    }

    return result;
}

export function offset (datetime, direction) {
    let result = parseDate(datetime);

    if (result) {
        if (direction == null) {
            direction = -1; // Convert into subscription time by default
        }
        const offset = offsetFromLocalTime(result);
        const timestamp = result.getTime();
        result = new Date(timestamp + direction * offset);
    }

    return result;
}

export function now () {
    return offset(new Date());
}

export function getElementFormat (format) {
    return elementFormats.find((elementFormat) => elementFormat.value === format);
}

export function getDateFormat (date, format) {
    if (!date) return;
    // Solves for the case where `date` as a timestamp String is not valid but other String formats are
    // ex. ISO Strings passed into `parseDate` fail _.isDate() but also do not parse correctly
    let newDate = new Date(date);
    if (newDate.toString() === 'Invalid Date') {
        newDate = parseDate(date);
    }
    if (!newDate) return;
    const dateConversion = getElementFormat(format);
    let formattedDate;

    if (!dateConversion) return newDate;

    switch (dateConversion.value) {
        case 'milliseconds':
            formattedDate = newDate.getTime();
            break;
        case 'w3c_iso8601':
            formattedDate = newDate.toISOString();
            break;
        case 'seconds':
            formattedDate = Math.floor(newDate.getTime() / 1000);
            break;
        case 'format:01/02/2006 03:04pm':
        case 'format:Mon Jan 02 2006 15:04:05 MST-0700':
        case 'format:2006-01-02T15:04:05-0700':
        case 'format:2006-01-02T15:04:05-07:00':
        case 'format:2006-01-02T15:04:05.999-07:00':
        case 'format:2006-01-02T15:04:05Z07:00':
        case 'format:2006-01-02 15:04:05 -0700':
            formattedDate = moment(newDate).format(dateConversion.format);
            break;
        case 'format:Mon Jan 02 2006':
        case 'format:2006-01-02T15:04:05.99Z':
        case 'format:2006-01-02T15:04:05':
        case 'format:2006-01-02 15:04:05 MST':
        case 'format:2006-01-02 15:04:05':
            // Return default date for now. There are edge-cases with these formats to be fixed in APP-30031
            formattedDate = newDate;
            break;
    }

    return formattedDate;
}

export function resetToMilliseconds (date, dateFormat) {
    if (!date) return;

    const dateConversion = getElementFormat(dateFormat);
    let milliDate;

    if (typeof date === 'number' && dateFormat === 'seconds') {
        // seconds timestamp does not parse to correct date, so we need to set back to milli timestamp
        // check if timestamp is already in milliseconds (ex. on-load timestamp has already been converted to ms)
        const stringDate = `${date}`;
        milliDate = stringDate.length === 13 ? date : date * 1000;
    } else if (dateConversion && dateConversion.invalidDateParse && typeof date === 'string') {
        if (!isNaN(date)) {
            // if timestamp string, keep current format
            milliDate = date;
        } else {
            milliDate = moment(date, dateConversion.format, { ignoreTimeZone: true });
        }
    } else {
        milliDate = date;
    }

    const reset = getDateFormat(milliDate, 'milliseconds');

    return reset;
}

export function subscriptionDateFilter (date, format = 'MMM D, YYYY', defaultDisplay = '--', timezone, eleFormat) {
    const hasTime = /h{1,2}:m{1,2}/; // weak check for format string with h:m, hh:m, h:mm, or hh:mm...
    if ([undefined, null, NaN, Infinity, -Infinity].includes(date)) return defaultDisplay;

    const dateConversion = getElementFormat(eleFormat);
    if (dateConversion) {
        date = resetToMilliseconds(date, eleFormat);
    }

    if (isDate(date) || isNumber(date)) {
        date = offset(date, -1);
    }

    const dateFormat = angularDateFilterFormatMap[format] || format;
    const momentDate = moment(date, { ignoreTimeZone: true });
    let formatted = momentDate.isValid() ? momentDate.format(dateFormat) : '--';

    let tz;
    if (momentDate.isValid()) {
        tz = moment(momentDate).zoneAbbr() || '';
    }

    // @var {Boolean} addTz
    const addTz = ['short', 'medium', 'shortTime', 'mediumTime'].includes(format) || hasTime.test(format);

    if (tz && tz.length && addTz) formatted += ` ${tz}`;

    return formatted;
}
